import { FC, useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  DateTemplate,
  DropdownTemplate,
  GroupTitleTemplate,
  MultiselectBoundTemplate,
  TemplateWrapper,
  TextboxTemplate,
} from "@ryde/controls";
import {
  IInjectedDrawerProps,
  IJourneyDuplicateProps,
  loadingEditFormContentAtomFamily,
} from "@ryde/shared";

import { LayoutRightLoader } from "../../../right";
import { CommonDefaultDrawer } from "../common-default-drawer";
import { BaseDrawer, DrawerFormWrapperStyled, FormFooter } from "../drawer";
import {
  JourneyDuplicateFormFieldsEnum,
  JourneysCreationTypeSelectList,
  useJourneyDuplicate,
} from "./parts";

export const JourneyDuplicateFormDrawer: FC<
  IJourneyDuplicateProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber, dto, assignRides }) => {
  const { t } = useTranslation();

  const isCrudOnlyDataLoading = useRecoilValue(
    loadingEditFormContentAtomFamily("journey"),
  );

  const { methods, isSaveDisabled, onSaveClick } = useJourneyDuplicate();

  const { setValue } = methods;

  const handleSaveClick = useEvent(() => {
    if (dto?.id) {
      onSaveClick(dto.id);
    }
  });

  useEffect(() => {
    if (dto) {
      const { name } = dto;

      setValue(JourneyDuplicateFormFieldsEnum.Name, name, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  }, [dto, setValue]);

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={`${t(`general.DUPLICATE`)}  ${t("journey.single")}`}
        onCloseDrawer={onCloseDrawer}
        Footer={
          <FormFooter
            cancelLabel={t("general.CLOSE")}
            onCancelClick={onCloseDrawer}
            onSaveClick={handleSaveClick}
            isSaveDisabled={isSaveDisabled}
          />
        }
      >
        <FormProvider {...methods}>
          {!isCrudOnlyDataLoading && (
            <DrawerFormWrapperStyled>
              <TemplateWrapper>
                <TextboxTemplate
                  name={JourneyDuplicateFormFieldsEnum.Name}
                  formLabel={t("journey.formLabel.name")}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <DropdownTemplate
                  name={JourneyDuplicateFormFieldsEnum.CreationSource}
                  formLabel={t("journey.formLabel.creationSource")}
                  formAttributes={{
                    disabled: true,
                    selectItems: JourneysCreationTypeSelectList,
                  }}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <GroupTitleTemplate
                  name={"assigned_rides_title"}
                  formLabel={t("journey.formLabel.assigned_rides_title")}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <MultiselectBoundTemplate
                  name={JourneyDuplicateFormFieldsEnum.DayGroup}
                  formLabel={t("journey.formLabel.dayGroup")}
                  formAttributes={{
                    dropItemsSource: "dayGroup",
                    dropItemsDisableAdd: true,
                  }}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <MultiselectBoundTemplate
                  name={JourneyDuplicateFormFieldsEnum.DayType}
                  formLabel={t("journey.formLabel.dayType")}
                  formAttributes={{
                    dropItemsSource: "dayType",
                    dropItemsDisableAdd: true,
                  }}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <DateTemplate
                  name={JourneyDuplicateFormFieldsEnum.DateFrom}
                  formLabel={t("journey.formLabel.dateFrom")}
                />
              </TemplateWrapper>

              <TemplateWrapper>
                <DateTemplate
                  name={JourneyDuplicateFormFieldsEnum.DateTo}
                  formLabel={t("journey.formLabel.dateTo")}
                />
              </TemplateWrapper>
            </DrawerFormWrapperStyled>
          )}

          {isCrudOnlyDataLoading && (
            <LayoutRightLoader>{t(`ui.LOADING_DATA`)}...</LayoutRightLoader>
          )}
        </FormProvider>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
